import React, { useState, useEffect, useCallback } from 'react';

interface Flashcard {
    word: string;
    definition: string;
    example?: string;
    difficulty: number;
}

interface VocabularyFlashcardsProps {
    onNavigateHome: () => void;
}

const VocabularyFlashcards: React.FC<VocabularyFlashcardsProps> = ({ onNavigateHome }) => {
    // Vocabulary data
    const vocabularyDatabase: { [key: number]: Flashcard[] } = {
        3: [
            { word: 'Adventure', definition: 'An exciting or dangerous experience', example: 'The treasure hunt was a great adventure!', difficulty: 3 },
            { word: 'Brave', definition: 'Having courage; not afraid', example: 'The brave knight faced the dragon.', difficulty: 3 },
            { word: 'Curious', definition: 'Eager to know or learn something', example: 'She was curious about the mysterious box.', difficulty: 3 },
            { word: 'Delicious', definition: 'Having a very pleasant taste', example: 'The cookies were absolutely delicious.', difficulty: 3 },
            { word: 'Enormous', definition: 'Very large in size or quantity', example: 'The elephant was enormous!', difficulty: 3 },
            { word: 'Fragile', definition: 'Easily broken or damaged', example: 'Handle the glass carefully; it\'s fragile.', difficulty: 3 },
            { word: 'Gigantic', definition: 'Extremely large', example: 'The gigantic tree towered over us.', difficulty: 3 },
            { word: 'Humble', definition: 'Not proud or boastful; modest', example: 'Despite winning, she remained humble.', difficulty: 3 },
            { word: 'Invisible', definition: 'Unable to be seen', example: 'The magician made the rabbit invisible.', difficulty: 3 },
            { word: 'Joyful', definition: 'Feeling great happiness', example: 'The children were joyful at the party.', difficulty: 3 },
            { word: 'Kind', definition: 'Having a friendly, generous nature', example: 'The kind teacher helped everyone.', difficulty: 3 },
            { word: 'Loyal', definition: 'Faithful and devoted', example: 'Dogs are known to be loyal pets.', difficulty: 3 },
            { word: 'Magical', definition: 'Having supernatural powers', example: 'The fairy had a magical wand.', difficulty: 3 },
            { word: 'Nervous', definition: 'Feeling worried or anxious', example: 'He felt nervous before the test.', difficulty: 3 },
            { word: 'Ordinary', definition: 'Normal; not special', example: 'It seemed like an ordinary day.', difficulty: 3 },
            { word: 'Peaceful', definition: 'Calm and quiet', example: 'The lake was peaceful in the morning.', difficulty: 3 },
            { word: 'Quick', definition: 'Moving fast', example: 'The quick rabbit hopped away.', difficulty: 3 },
            { word: 'Rough', definition: 'Having an uneven surface', example: 'The tree bark felt rough.', difficulty: 3 },
            { word: 'Shiny', definition: 'Reflecting light brightly', example: 'The shiny coin sparkled.', difficulty: 3 },
            { word: 'Tiny', definition: 'Very small', example: 'The tiny ant carried a big crumb.', difficulty: 3 }
        ],
        6: [
            { word: 'Abundant', definition: 'Existing in large quantities; plentiful', example: 'The forest had abundant wildlife.', difficulty: 6 },
            { word: 'Bewildered', definition: 'Completely puzzled or confused', example: 'She was bewildered by the complex math problem.', difficulty: 6 },
            { word: 'Compassionate', definition: 'Feeling sympathy and concern for others', example: 'The compassionate nurse comforted the patient.', difficulty: 6 },
            { word: 'Diligent', definition: 'Working hard and carefully', example: 'The diligent student studied every night.', difficulty: 6 },
            { word: 'Eloquent', definition: 'Speaking fluently and persuasively', example: 'The eloquent speaker captivated the audience.', difficulty: 6 },
            { word: 'Formidable', definition: 'Inspiring fear through being impressively powerful', example: 'The castle had formidable defenses.', difficulty: 6 },
            { word: 'Gracious', definition: 'Courteous, kind, and pleasant', example: 'The gracious host welcomed everyone warmly.', difficulty: 6 },
            { word: 'Harmonious', definition: 'Forming a pleasing combination', example: 'The choir sang in harmonious voices.', difficulty: 6 },
            { word: 'Innovative', definition: 'Introducing new ideas or methods', example: 'The innovative design won first prize.', difficulty: 6 },
            { word: 'Jubilant', definition: 'Feeling great joy and triumph', example: 'The team was jubilant after their victory.', difficulty: 6 },
            { word: 'Knowledgeable', definition: 'Well-informed and educated', example: 'The knowledgeable guide explained everything.', difficulty: 6 },
            { word: 'Luminous', definition: 'Giving off light; bright', example: 'The luminous moon lit up the night.', difficulty: 6 },
            { word: 'Magnificent', definition: 'Extremely beautiful and impressive', example: 'The magnificent cathedral amazed visitors.', difficulty: 6 },
            { word: 'Nonchalant', definition: 'Casually calm and relaxed', example: 'He remained nonchalant despite the chaos.', difficulty: 6 },
            { word: 'Optimistic', definition: 'Hopeful about the future', example: 'She stayed optimistic during tough times.', difficulty: 6 },
            { word: 'Persistent', definition: 'Continuing firmly despite difficulty', example: 'His persistent efforts finally paid off.', difficulty: 6 },
            { word: 'Quaint', definition: 'Attractively old-fashioned', example: 'The quaint village had cobblestone streets.', difficulty: 6 },
            { word: 'Resilient', definition: 'Able to recover quickly from difficulties', example: 'Children are remarkably resilient.', difficulty: 6 },
            { word: 'Sophisticated', definition: 'Complex or refined', example: 'The sophisticated robot could think like humans.', difficulty: 6 },
            { word: 'Tranquil', definition: 'Free from disturbance; peaceful', example: 'The tranquil garden was perfect for meditation.', difficulty: 6 }
        ],
        9: [
            { word: 'Ambiguous', definition: 'Having more than one possible meaning', example: 'The ambiguous statement confused everyone.', difficulty: 9 },
            { word: 'Benevolent', definition: 'Well-meaning and kindly', example: 'The benevolent king cared for his people.', difficulty: 9 },
            { word: 'Contemplate', definition: 'To think deeply about something', example: 'She liked to contemplate life\'s mysteries.', difficulty: 9 },
            { word: 'Desolate', definition: 'Empty and giving an impression of sadness', example: 'The desolate landscape stretched for miles.', difficulty: 9 },
            { word: 'Eloquence', definition: 'Fluent and persuasive speaking or writing', example: 'His eloquence moved the entire audience.', difficulty: 9 },
            { word: 'Formulate', definition: 'To create or devise methodically', example: 'Scientists formulate hypotheses before testing.', difficulty: 9 },
            { word: 'Gregarious', definition: 'Fond of company; sociable', example: 'Her gregarious nature made her popular.', difficulty: 9 },
            { word: 'Hypothesis', definition: 'A proposed explanation for a phenomenon', example: 'The scientist tested her hypothesis carefully.', difficulty: 9 },
            { word: 'Indigenous', definition: 'Originating naturally in a particular place', example: 'The indigenous plants thrived in the climate.', difficulty: 9 },
            { word: 'Juxtapose', definition: 'To place side by side for comparison', example: 'The artist juxtaposed light and shadow.', difficulty: 9 },
            { word: 'Kinetic', definition: 'Relating to motion', example: 'Kinetic energy increases with speed.', difficulty: 9 },
            { word: 'Lucid', definition: 'Clear and easy to understand', example: 'Her lucid explanation helped everyone understand.', difficulty: 9 },
            { word: 'Melancholy', definition: 'A thoughtful sadness', example: 'The melancholy music touched her heart.', difficulty: 9 },
            { word: 'Nostalgic', definition: 'Longing for the past', example: 'The old photographs made him nostalgic.', difficulty: 9 },
            { word: 'Ostentatious', definition: 'Showy in a vulgar way', example: 'His ostentatious display of wealth was embarrassing.', difficulty: 9 },
            { word: 'Paradox', definition: 'A seemingly contradictory statement', example: 'It\'s a paradox that silence can speak volumes.', difficulty: 9 },
            { word: 'Quintessential', definition: 'Representing the perfect example', example: 'She was the quintessential student leader.', difficulty: 9 },
            { word: 'Retrospective', definition: 'Looking back on past events', example: 'In retrospective, the decision was wise.', difficulty: 9 },
            { word: 'Serendipity', definition: 'Pleasant surprise; fortunate accident', example: 'Meeting her was pure serendipity.', difficulty: 9 },
            { word: 'Tangible', definition: 'Clear and definite; able to be touched', example: 'The benefits of exercise are tangible.', difficulty: 9 }
        ],
        12: [
            { word: 'Acrimonious', definition: 'Harsh and bitter in manner or speech', example: 'The acrimonious debate divided the committee.', difficulty: 12 },
            { word: 'Bureaucratic', definition: 'Involving complex administrative procedures', example: 'The bureaucratic process took months to complete.', difficulty: 12 },
            { word: 'Circumspect', definition: 'Careful and cautious in action', example: 'The diplomat was circumspect in his negotiations.', difficulty: 12 },
            { word: 'Dichotomy', definition: 'A division into two contrasting parts', example: 'There\'s a dichotomy between theory and practice.', difficulty: 12 },
            { word: 'Ephemeral', definition: 'Lasting for a very short time', example: 'The beauty of cherry blossoms is ephemeral.', difficulty: 12 },
            { word: 'Facetious', definition: 'Treating serious issues with inappropriate humor', example: 'His facetious comments were poorly timed.', difficulty: 12 },
            { word: 'Gregarious', definition: 'Sociable and outgoing', example: 'Her gregarious personality made networking easy.', difficulty: 12 },
            { word: 'Hegemony', definition: 'Leadership or dominance over others', example: 'The country sought hegemony in the region.', difficulty: 12 },
            { word: 'Ineffable', definition: 'Too great to be expressed in words', example: 'The ineffable beauty of the sunset moved her to tears.', difficulty: 12 },
            { word: 'Juxtaposition', definition: 'Placing things side by side for contrast', example: 'The juxtaposition of wealth and poverty was striking.', difficulty: 12 },
            { word: 'Kaleidoscopic', definition: 'Constantly changing and colorful', example: 'The kaleidoscopic city lights dazzled tourists.', difficulty: 12 },
            { word: 'Labyrinthine', definition: 'Complex and confusing like a maze', example: 'The labyrinthine legal system frustrated citizens.', difficulty: 12 },
            { word: 'Machiavellian', definition: 'Cunning and scheming in politics', example: 'His Machiavellian tactics ensured his victory.', difficulty: 12 },
            { word: 'Nomenclature', definition: 'A system of names used in a field', example: 'Scientific nomenclature follows strict rules.', difficulty: 12 },
            { word: 'Obsequious', definition: 'Excessively eager to please', example: 'The obsequious assistant agreed with everything.', difficulty: 12 },
            { word: 'Paradigm', definition: 'A typical example or model', example: 'This discovery could shift the scientific paradigm.', difficulty: 12 },
            { word: 'Quixotic', definition: 'Unrealistic but idealistic', example: 'His quixotic quest for perfection was admirable.', difficulty: 12 },
            { word: 'Recalcitrant', definition: 'Stubbornly defiant', example: 'The recalcitrant student refused to follow rules.', difficulty: 12 },
            { word: 'Sanguine', definition: 'Optimistic in a difficult situation', example: 'Despite setbacks, she remained sanguine.', difficulty: 12 },
            { word: 'Taciturn', definition: 'Reserved in speech; saying little', example: 'The taciturn professor rarely spoke in meetings.', difficulty: 12 }
        ]
    };

    // State management - similar to grammar flashcards
    const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
    const [currentCards, setCurrentCards] = useState<Flashcard[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showingDefinition, setShowingDefinition] = useState(false);
    const [showingExample, setShowingExample] = useState(false);
    const [points, setPoints] = useState(0);
    const [streak, setStreak] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [totalAnswered, setTotalAnswered] = useState(0);
    const [level, setLevel] = useState(1);

    // Level data similar to grammar flashcards
    const levelData = {
        3: { name: 'Young Explorer', icon: '🌱', color: '#FF6B35', description: 'Fun words for curious minds' },
        6: { name: 'Word Warrior', icon: '🌿', color: '#2ECC71', description: 'Build your vocabulary arsenal' },
        9: { name: 'Vocab Master', icon: '🌳', color: '#3498DB', description: 'Advanced word power' },
        12: { name: 'Language Legend', icon: '🦅', color: '#9B59B6', description: 'Elite vocabulary skills' }
    };

    // Level up effect
    useEffect(() => {
        const newLevel = Math.floor(points / 100) + 1;
        if (newLevel > level) {
            setLevel(newLevel);
        }
    }, [points, level]);

    // Select a grade level and start session
    const selectLevel = useCallback((levelNum: number) => {
        const levelCards = vocabularyDatabase[levelNum];
        
        if (levelCards.length === 0) {
            alert(`No cards available for Level ${levelNum} yet. Try another level!`);
            return;
        }

        console.log(`Loading ${levelCards.length} cards for level ${levelNum}`);
        
        setSelectedLevel(levelNum);
        setCurrentCards(levelCards);
        setCurrentIndex(0);
        setShowingDefinition(false);
        setShowingExample(false);
    }, []);

    // Handle revealing definition
    const handleRevealDefinition = useCallback(() => {
        if (!showingDefinition) {
            setShowingDefinition(true);
            setPoints(prev => prev + 5);
            setStreak(prev => prev + 1);
            setTotalAnswered(prev => prev + 1);
            setCorrectAnswers(prev => prev + 1);
        }
    }, [showingDefinition]);

    // Navigation methods
    const nextCard = useCallback(() => {
        if (currentIndex < currentCards.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setShowingDefinition(false);
            setShowingExample(false);
        }
    }, [currentIndex, currentCards.length]);

    const previousCard = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setShowingDefinition(false);
            setShowingExample(false);
        }
    }, [currentIndex]);

    const toggleExample = useCallback(() => {
        setShowingExample(prev => !prev);
        if (!showingExample) {
            setPoints(prev => prev + 3);
        }
    }, [showingExample]);

    const resetToLevelSelection = useCallback(() => {
        setSelectedLevel(null);
        setCurrentCards([]);
        setCurrentIndex(0);
        setShowingDefinition(false);
        setShowingExample(false);
    }, []);

    // Helper functions
    const getStreakMessage = useCallback(() => {
        if (streak >= 15) return "🔥 VOCAB MASTER! 🔥";
        if (streak >= 10) return "⚡ SUPER STREAK! ⚡";
        if (streak >= 5) return "🌟 Great Streak! 🌟";
        if (streak >= 3) return "✨ Nice Going! ✨";
        return "";
    }, [streak]);

    const getAccuracy = useCallback(() => {
        return totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0;
    }, [totalAnswered, correctAnswers]);

    // Level Selection Screen
    if (!selectedLevel) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '20px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                {/* Stats Header */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    padding: '20px',
                    marginBottom: '30px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                        gap: '20px',
                        marginBottom: '15px'
                    }}>
                        <div style={{ textAlign: 'center', color: 'white' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>⭐ {points}</div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Points</div>
                        </div>
                        <div style={{ textAlign: 'center', color: 'white' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>🔥 {streak}</div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Streak</div>
                        </div>
                        <div style={{ textAlign: 'center', color: 'white' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>🎯 {getAccuracy()}%</div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Accuracy</div>
                        </div>
                        <div style={{ textAlign: 'center', color: 'white' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>🏆 {level}</div>
                            <div style={{ fontSize: '14px', opacity: 0.8 }}>Level</div>
                        </div>
                    </div>
                    {getStreakMessage() && (
                        <div style={{
                            textAlign: 'center',
                            color: '#FFD700',
                            fontSize: '18px',
                            fontWeight: 'bold'
                        }}>
                            {getStreakMessage()}
                        </div>
                    )}
                </div>

                <h1 style={{
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '3rem',
                    margin: '20px 0 10px',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                }}>📚 Word Heroes 📚</h1>
                
                <p style={{
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '1.2rem',
                    marginBottom: '10px',
                    opacity: 0.9
                }}>Master the Power of Words!</p>
                
                <p style={{
                    textAlign: 'center',
                    color: 'white',
                    fontSize: '1rem',
                    marginBottom: '40px',
                    opacity: 0.8
                }}>Choose your skill level and start your vocabulary adventure</p>

                {/* Level Cards */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '30px',
                    maxWidth: '1200px',
                    margin: '0 auto 40px'
                }}>
                    {[3, 6, 9, 12].map(grade => {
                        const cardCount = vocabularyDatabase[grade].length;
                        const data = levelData[grade as keyof typeof levelData];
                        
                        return (
                            <div
                                key={grade}
                                onClick={() => cardCount > 0 ? selectLevel(grade) : undefined}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '20px',
                                    padding: '30px',
                                    textAlign: 'center',
                                    cursor: cardCount > 0 ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.3s ease',
                                    border: `2px solid ${data.color}`,
                                    opacity: cardCount > 0 ? 1 : 0.6,
                                    transform: 'translateY(0)',
                                }}
                                onMouseEnter={(e) => {
                                    if (cardCount > 0) {
                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                        e.currentTarget.style.boxShadow = `0 10px 30px rgba(0,0,0,0.2)`;
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{ fontSize: '4rem', marginBottom: '15px' }}>{data.icon}</div>
                                <h3 style={{ color: 'white', margin: '10px 0', fontSize: '1.5rem' }}>Grade {grade}</h3>
                                <h4 style={{ color: data.color, margin: '5px 0', fontSize: '1.2rem' }}>{data.name}</h4>
                                <p style={{ color: 'white', opacity: 0.8, margin: '15px 0', fontSize: '0.95rem' }}>{data.description}</p>
                                <p style={{
                                    color: cardCount > 0 ? '#4CAF50' : '#FF6B6B',
                                    fontWeight: 'bold',
                                    fontSize: '1rem'
                                }}>
                                    {cardCount > 0 ? `${cardCount} words await!` : 'Coming soon!'}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <div style={{ textAlign: 'center' }}>
                    <button 
                        onClick={onNavigateHome}
                        style={{
                            background: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '25px',
                            padding: '15px 30px',
                            fontSize: '1.1rem',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            backdropFilter: 'blur(10px)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                        }}
                    >
                        🏠 Return to Home Base
                    </button>
                </div>
            </div>
        );
    }

    // Quiz Interface
    const currentCard = currentCards[currentIndex];
    const progressPercentage = ((currentIndex + 1) / currentCards.length) * 100;

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            {/* Header with Stats */}
            <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                borderRadius: '15px',
                padding: '20px',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '20px'
            }}>
                <div>
                    <h2 style={{ color: 'white', margin: '0 0 5px 0', fontSize: '1.5rem' }}>Grade {selectedLevel} Vocabulary</h2>
                    <div style={{ color: 'white', opacity: 0.8, fontSize: '1rem' }}>
                        Word {currentIndex + 1} of {currentCards.length}
                    </div>
                </div>
                
                <div style={{ display: 'flex', gap: '30px' }}>
                    <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>⭐ {points}</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Points</div>
                    </div>
                    <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>🔥 {streak}</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Streak</div>
                    </div>
                    <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>🎯 {getAccuracy()}%</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Accuracy</div>
                    </div>
                    <div style={{ textAlign: 'center', color: 'white' }}>
                        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>🏆 {level}</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Level</div>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div style={{
                background: 'rgba(255, 255, 255, 0.2)',
                height: '8px',
                borderRadius: '4px',
                marginBottom: '30px',
                overflow: 'hidden'
            }}>
                <div style={{
                    background: 'linear-gradient(90deg, #4CAF50, #45a049)',
                    height: '100%',
                    width: `${progressPercentage}%`,
                    transition: 'width 0.3s ease'
                }} />
            </div>

            {/* Vocabulary Card with Flip Animation */}
            <div style={{
                maxWidth: '480px',
                margin: '0 auto 30px',
                height: '380px', // Reduced height
                perspective: '1000px'
            }}>
                <div
                    onClick={() => !showingDefinition && handleRevealDefinition()}
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        transformStyle: 'preserve-3d',
                        transition: 'transform 0.6s',
                        transform: showingDefinition ? 'rotateY(180deg)' : 'rotateY(0deg)',
                        cursor: !showingDefinition ? 'pointer' : 'default'
                    }}
                >
                    {/* Front of Card (Word) */}
                    <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '20px',
                        padding: '30px', // Reduced padding
                        textAlign: 'center',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        {/* Card indicators */}
                        <div style={{
                            position: 'absolute',
                            top: '15px',
                            left: '20px',
                            background: '#667eea',
                            color: 'white',
                            borderRadius: '20px',
                            padding: '5px 12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}>
                            {currentIndex + 1}/{currentCards.length}
                        </div>

                        <div style={{
                            position: 'absolute',
                            top: '15px',
                            right: '20px',
                            display: 'flex',
                            gap: '3px'
                        }}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <div
                                    key={i}
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: i <= currentCard.difficulty ? '#FF6B35' : '#e0e0e0'
                                    }}
                                />
                            ))}
                        </div>

                        <h3 style={{
                            fontSize: '2.8rem', // Reduced size
                            margin: '0 0 25px 0',
                            color: '#333',
                            fontWeight: 'bold'
                        }}>
                            {currentCard.word}
                        </h3>

                        <div style={{
                            background: '#f8f9fa',
                            borderRadius: '15px',
                            padding: '18px', // Reduced padding
                            color: '#666',
                            fontSize: '1.1rem',
                            maxWidth: '350px'
                        }}>
                            What does this word mean?
                        </div>

                        <div style={{
                            marginTop: '30px',
                            fontSize: '1rem',
                            color: '#999',
                            fontStyle: 'italic'
                        }}>
                            Click to reveal definition
                        </div>
                    </div>

                    {/* Back of Card (Definition) */}
                    <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backfaceVisibility: 'hidden',
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '20px',
                        padding: '30px', // Reduced padding
                        textAlign: 'center',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                        transform: 'rotateY(180deg)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'auto'
                    }}>
                        {/* Card indicators */}
                        <div style={{
                            position: 'absolute',
                            top: '15px',
                            left: '20px',
                            background: '#667eea',
                            color: 'white',
                            borderRadius: '20px',
                            padding: '5px 12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}>
                            {currentIndex + 1}/{currentCards.length}
                        </div>

                        <div style={{
                            position: 'absolute',
                            top: '15px',
                            right: '20px',
                            display: 'flex',
                            gap: '3px'
                        }}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <div
                                    key={i}
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: i <= currentCard.difficulty ? '#FF6B35' : '#e0e0e0'
                                    }}
                                />
                            ))}
                        </div>

                        <h3 style={{
                            fontSize: '2.5rem',
                            margin: '0 0 20px 0',
                            color: '#333',
                            fontWeight: 'bold'
                        }}>
                            {currentCard.word}
                        </h3>

                        <div style={{
                            background: '#e8f5e8',
                            borderRadius: '15px',
                            padding: '20px',
                            margin: '0 0 20px 0',
                            borderLeft: '4px solid #4CAF50',
                            maxWidth: '450px'
                        }}>
                            <div style={{ fontSize: '0.9rem', color: '#28a745', marginBottom: '8px', fontWeight: 'bold' }}>
                                Definition:
                            </div>
                            <div style={{
                                fontSize: '1.2rem',
                                color: '#333',
                                lineHeight: 1.5
                            }}>
                                {currentCard.definition}
                            </div>
                        </div>

                        {/* Example Section */}
                        {showingExample && currentCard.example && (
                            <div style={{
                                background: '#fff3cd',
                                borderRadius: '15px',
                                padding: '15px',
                                margin: '0 0 20px 0',
                                borderLeft: '4px solid #ffc107',
                                maxWidth: '450px'
                            }}>
                                <div style={{ fontSize: '0.9rem', color: '#856404', marginBottom: '8px', fontWeight: 'bold' }}>
                                    💡 Example:
                                </div>
                                <div style={{ fontSize: '1rem', color: '#333', fontStyle: 'italic' }}>
                                    "{currentCard.example}"
                                </div>
                            </div>
                        )}

                        {/* Example Button */}
                        {currentCard.example && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExample();
                                }}
                                style={{
                                    background: showingExample 
                                        ? 'linear-gradient(135deg, #28a745, #20c997)' 
                                        : 'linear-gradient(135deg, #ffc107, #e0a800)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '25px',
                                    padding: '12px 25px',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    fontWeight: 'bold',
                                    position: 'absolute',
                                    bottom: '20px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                {showingExample ? '✅ Example Shown (+3 pts)' : '💡 Show Example'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Navigation Controls */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                maxWidth: '600px',
                margin: '0 auto 30px',
                gap: '20px'
            }}>
                <button
                    onClick={previousCard}
                    disabled={currentIndex === 0}
                    style={{
                        background: currentIndex === 0 ? '#ccc' : 'rgba(255, 255, 255, 0.2)',
                        color: currentIndex === 0 ? '#666' : 'white',
                        border: 'none',
                        borderRadius: '25px',
                        padding: '12px 25px',
                        fontSize: '1rem',
                        cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    ⬅️ Previous
                </button>
                
                <button
                    onClick={nextCard}
                    disabled={currentIndex === currentCards.length - 1}
                    style={{
                        background: currentIndex === currentCards.length - 1 ? '#ccc' : 'rgba(255, 255, 255, 0.2)',
                        color: currentIndex === currentCards.length - 1 ? '#666' : 'white',
                        border: 'none',
                        borderRadius: '25px',
                        padding: '12px 25px',
                        fontSize: '1rem',
                        cursor: currentIndex === currentCards.length - 1 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    Next ➡️
                </button>
            </div>

            {/* Bottom Navigation */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                flexWrap: 'wrap'
            }}>
                <button
                    onClick={resetToLevelSelection}
                    style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '25px',
                        padding: '12px 25px',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }}
                >
                    🔄 Change Level
                </button>
                
                <button
                    onClick={onNavigateHome}
                    style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '25px',
                        padding: '12px 25px',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }}
                >
                    🏠 Home Base
                </button>
            </div>

            {/* Completion Celebration */}
            {currentIndex === currentCards.length - 1 && showingDefinition && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '20px',
                        padding: '40px',
                        textAlign: 'center',
                        maxWidth: '400px',
                        margin: '20px'
                    }}>
                        <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎉</div>
                        <h2 style={{ color: '#333', marginBottom: '15px' }}>Congratulations!</h2>
                        <p style={{ color: '#666', marginBottom: '20px' }}>
                            You've completed all Grade {selectedLevel} vocabulary cards!
                        </p>
                        <div style={{ 
                            background: '#f8f9fa', 
                            borderRadius: '10px', 
                            padding: '15px', 
                            marginBottom: '30px',
                            color: '#333'
                        }}>
                            <div>Final Score: ⭐ {points} points</div>
                            <div>Accuracy: 🎯 {getAccuracy()}%</div>
                        </div>
                        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                            <button
                                onClick={resetToLevelSelection}
                                style={{
                                    background: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '25px',
                                    padding: '12px 20px',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                }}
                            >
                                🔄 Try Another Level
                            </button>
                            <button
                                onClick={onNavigateHome}
                                style={{
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '25px',
                                    padding: '12px 20px',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer'
                                }}
                            >
                                🏠 Go Home
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VocabularyFlashcards;